import { OperatorType, ShopsManufacturers } from '@/types/generated-types/graphql';
import { UserState } from '@/composables/User';
import { getAuthHeaders } from '@/composables/nestApi';

const fields = ['id_shop_manufacturer', 'id_manufacturer', 'name'];
const fieldsName = fields.reduce((acc: string, current: string) => `${acc} ${current}`);

// AbortController pour annuler les requêtes en cours
let currentAbortController: AbortController | null = null;

async function executeGraphQL(
  filters: any[],
  limit: number,
  offset: number,
  signal: AbortSignal,
): Promise<ShopsManufacturers[]> {
  const query = `query ($limit: Int!, $offset: Int!, $filter: [FilterParameters]) {
    ShopsManufacturersSelection (params: {limit: $limit, offset: $offset, filter: $filter}) {
      items { ${fieldsName} }
    }
  }`;

  const response = await fetch('/api/graphql', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({
      query,
      variables: { limit, offset, filter: filters },
    }),
    signal,
  });

  const result = await response.json();
  return result.data?.ShopsManufacturersSelection?.items ?? [];
}

async function searchByName(
  name: string,
  limit: number,
  offset: number,
  signal: AbortSignal,
): Promise<ShopsManufacturers[]> {
  const filters = [
    { field: 'name', operator: OperatorType.Against, value: name },
    { field: 'id_shop', operator: OperatorType.Equals, value: UserState.activeShop?.id },
  ];

  return executeGraphQL(filters, limit, offset, signal);
}

async function searchByManufacturerId(
  manufacturerId: string,
  signal: AbortSignal,
): Promise<ShopsManufacturers[]> {
  const filters = [
    { field: 'id_manufacturer', operator: OperatorType.Equals, value: manufacturerId },
    { field: 'id_shop', operator: OperatorType.Equals, value: UserState.activeShop?.id },
  ];

  return executeGraphQL(filters, 1, 0, signal);
}

export default async function ShopsManufacturersList(name: string, limit = 50, offset = 0): Promise<Array<ShopsManufacturers>|null> {
  // Annuler la requête précédente
  if (currentAbortController) {
    currentAbortController.abort();
  }
  currentAbortController = new AbortController();
  const { signal } = currentAbortController;

  const searchTerm = name.trim();

  // Ne pas lancer la recherche si le terme est vide
  if (!searchTerm) {
    return [];
  }

  const isSingleWord = !searchTerm.includes(' ');

  try {
    // Si un seul mot et première page : recherche par nom ET par id_manufacturer en parallèle
    if (isSingleWord && offset === 0) {
      const [nameResults, idResults] = await Promise.all([
        searchByName(searchTerm, limit, offset, signal),
        searchByManufacturerId(searchTerm, signal),
      ]);

      // Fusionner : id_manufacturer en premier, puis les résultats par nom (sans doublons)
      const mergedResults = [...idResults];
      nameResults.forEach((manufacturer) => {
        if (!mergedResults.some((m) => m.id_shop_manufacturer === manufacturer.id_shop_manufacturer)) {
          mergedResults.push(manufacturer);
        }
      });

      return mergedResults.slice(0, limit);
    }

    // Recherche standard par nom uniquement
    return searchByName(searchTerm, limit, offset, signal);
  } catch (error: any) {
    // Ignorer les erreurs d'abort (requête annulée)
    if (error.name === 'AbortError') {
      return null;
    }
    throw error;
  }
}
